import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Button } from "@mui/material";
import { isEmpty } from "lodash";
import { TiTimes, TiTrash } from "react-icons/ti";
import helpers from "utils/helpers";

const PRIMARY = "#8FC150";
const PRIMARY_HOVER = "#7eae43";

const modalSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "900px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const emptyOption = () => ({ text: "", textAr: "" });

const emptyForm = () => ({
  question: "",
  questionAr: "",
  type: "text",
  options: [],
});

const normalizeOptions = (options = []) =>
  options.map((o) =>
    typeof o === "string"
      ? { text: o, textAr: "" }
      : { text: o?.text || "", textAr: o?.textAr || "" },
  );

const QuestionsStep = ({ permission, user }) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const canEdit = permission?.add || user?.role === "admin";
  const questions = watch("questions") || [];

  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const closeModal = () => {
    setOpen(false);
    setEditIndex(null);
    setForm(emptyForm());
  };

  const openAdd = () => {
    setEditIndex(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (index) => {
    const q = questions[index];
    setEditIndex(index);
    setForm({
      _id: q?._id,
      question: q?.question || "",
      questionAr: q?.questionAr || "",
      type: q?.type === "dropdown" ? "dropdown" : "text",
      options: normalizeOptions(q?.options),
    });
    setOpen(true);
  };

  const isValid = () => {
    if (isEmpty(form.question?.trim())) return false;
    if (form.type === "dropdown") {
      if (!form.options.length) return false;
      if (form.options.some((o) => isEmpty(String(o?.text || "").trim())))
        return false;
      if (
        form.options.filter((o) => String(o?.text || "").trim()).length < 2
      )
        return false;
    }
    return true;
  };

  const saveQuestion = () => {
    if (!isValid()) return;
    const existing = editIndex !== null ? questions[editIndex] : null;
    const nextQuestion = {
      ...(existing?._id || form._id ? { _id: existing?._id || form._id } : {}),
      question: form.question.trim(),
      questionAr: String(form.questionAr || "").trim(),
      type: form.type === "dropdown" ? "dropdown" : "text",
      options:
        form.type === "dropdown"
          ? form.options
              .map((o) => ({
                text: String(o?.text || "").trim(),
                textAr: String(o?.textAr || "").trim(),
              }))
              .filter((o) => o.text)
          : [],
    };
    const next =
      editIndex === null
        ? [...questions, nextQuestion]
        : questions.map((q, i) => (i === editIndex ? nextQuestion : q));
    setValue("questions", next, { shouldDirty: true });
    closeModal();
  };

  const deleteQuestion = (index) => {
    setValue(
      "questions",
      questions.filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  const addOption = () => {
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, emptyOption()],
    }));
  };

  const canAddOption = () => {
    if (form.options.some((o) => isEmpty(String(o?.text || "").trim())))
      return false;
    if (form.options.length >= 10) return false;
    return true;
  };

  return (
    <div className="flex flex-col gap-4">
      <QuestionsTable
        questions={questions}
        canEdit={canEdit}
        onEdit={openEdit}
        onDelete={deleteQuestion}
      />

      {canEdit ? (
        <Button
          type="button"
          variant="contained"
          className="self-center"
          sx={{
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: PRIMARY_HOVER },
          }}
          onClick={openAdd}
        >
          {t("O_ADD_QUESTION")}
        </Button>
      ) : null}

      <QuestionModal
        open={open}
        form={form}
        setForm={setForm}
        isEdit={editIndex !== null}
        isValid={isValid}
        canAddOption={canAddOption}
        addOption={addOption}
        onClose={closeModal}
        onSave={saveQuestion}
      />
    </div>
  );
};

function QuestionsTable({ questions, canEdit, onEdit, onDelete }) {
  const { t } = useTranslation();
  if (!questions?.length || isEmpty(questions)) return null;

  return (
    <table className="w-full text-sm text-left text-black">
      <thead className="text-sm border border-[#E1E6EE] bg-gray-700 text-white">
        <tr className="py-3 px-6">
          <th className="py-3 px-6">{t("Question") || "Question"}</th>
          <th className="py-3 px-6">
            {(t("Question") || "Question") + (t("O_ARABIC") || " (Ar)")}
          </th>
          <th className="py-3 px-6">{t("O_TYPE")}</th>
          <th className="py-3 px-6">{t("Dropdown options") || "Options"}</th>
          {canEdit ? <th className="py-3 px-6">{t("O_ACTION")}</th> : null}
        </tr>
      </thead>
      <tbody>
        {questions.map((question, index) => (
          <tr className="py-3 px-6 border-b border-[#E1E6EE]" key={index}>
            <td className="py-3 px-6">{question.question || "-"}</td>
            <td className="py-3 px-6" dir="rtl">
              {question.questionAr || "-"}
            </td>
            <td className="py-3 px-6">
              {question.type === "dropdown"
                ? t("Dropdown options") || "Dropdown"
                : t("Text answer") || "Text"}
            </td>
            <td className="py-3 px-6">
              {question.type === "dropdown"
                ? (question.options || [])
                    .map((o) => (typeof o === "string" ? o : o?.text))
                    .filter(Boolean)
                    .join(", ") || "-"
                : "-"}
            </td>
            {canEdit ? (
              <td className="py-3 px-6">
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="bg-[#8FC150] hover:bg-[#7eae43] transition-colors duration-150 text-white px-3 py-2 rounded-md text-xs"
                    onClick={() => onEdit(index)}
                  >
                    {t("O_EDIT")}
                  </button>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-700 transition-colors duration-150 text-white px-4 py-2 rounded-md"
                    onClick={() =>
                      helpers.alertFunction(
                        `Are you sure want to delete ${
                          question.question || "this question"
                        }?`,
                        index,
                        onDelete,
                        true,
                      )
                    }
                  >
                    <TiTrash />
                  </button>
                </div>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function QuestionModal({
  open,
  form,
  setForm,
  isEdit,
  isValid,
  canAddOption,
  addOption,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="career-question-modal-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
    >
      <Box sx={modalSx}>
        <div className="flex flex-col h-full max-h-[90vh]">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2
              id="career-question-modal-title"
              className="text-2xl font-semibold text-gray-800"
            >
              {isEdit ? t("O_EDIT_QUESTION") : t("O_ADD_QUESTION")}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-200 flex items-center justify-center"
              type="button"
              aria-label="Close"
            >
              <TiTimes size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <div className="py-3 mb-1 flex-1">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    {t("Question") || "Question"}{" "}
                    <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    type="text"
                    placeholder={t("Enter question") || "Enter question"}
                    value={form.question}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        question: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="py-3 mb-1 flex-1">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    {(t("Question") || "Question") + (t("O_ARABIC") || " (Ar)")}
                  </label>
                  <input
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    type="text"
                    dir="rtl"
                    placeholder={
                      t("Enter Arabic question") || "Enter Arabic question"
                    }
                    value={form.questionAr}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        questionAr: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <select
                name="type"
                id="type"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={form.type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    type: e.target.value,
                    options: e.target.value === "dropdown" ? prev.options : [],
                  }))
                }
              >
                <option value="text">{t("Text answer") || "Text answer"}</option>
                <option value="dropdown">
                  {t("Dropdown options") || "Dropdown options"}
                </option>
              </select>

              {form.type === "dropdown" ? (
                <div className="flex flex-col gap-4">
                  {form.options.map((option, index) => (
                    <OptionCard
                      key={index}
                      option={option}
                      index={index}
                      options={form.options}
                      setOptions={(next) =>
                        setForm((prev) => ({ ...prev, options: next }))
                      }
                    />
                  ))}
                  <Button
                    type="button"
                    variant="contained"
                    disabled={!canAddOption()}
                    sx={{
                      backgroundColor: canAddOption() ? PRIMARY : undefined,
                      "&:hover": {
                        backgroundColor: canAddOption()
                          ? PRIMARY_HOVER
                          : undefined,
                      },
                      alignSelf: "flex-end",
                      whiteSpace: "nowrap",
                    }}
                    onClick={addOption}
                  >
                    {t("O_ADD_OPTION")}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {t("Applicants will type their own answer.") ||
                    "Applicants will type their own answer."}
                </p>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium"
              onClick={onClose}
            >
              {t("O_CANCEL") || "Cancel"}
            </button>
            <button
              type="button"
              className={`px-6 py-2.5 rounded-lg transition-colors duration-200 font-medium shadow-sm ${
                !isValid()
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-[#8FC150] text-white hover:bg-[#7eae43]"
              }`}
              disabled={!isValid()}
              onClick={onSave}
            >
              {isEdit ? t("O_UPDATE") || "Update" : t("O_ADD_QUESTION")}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

function OptionCard({ option, index, options, setOptions }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-0">
      {index > 0 && <hr className="border-gray-200 mb-2" />}
      <h3 className="text-md font-bold text-gray-700">
        {t("O_OPTION")} {index + 1}
      </h3>
      <div className="flex flex-row gap-4 items-center">
        <button
          type="button"
          className="bg-red-500 text-white p-2 rounded-full"
          onClick={() => setOptions(options.filter((_, i) => i !== index))}
        >
          <TiTrash size={20} />
        </button>
        <input
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          type="text"
          placeholder={t("O_TITLE")}
          value={option?.text || ""}
          onChange={(e) =>
            setOptions(
              options.map((o, i) =>
                i === index ? { ...o, text: e.target.value } : o,
              ),
            )
          }
        />
        <input
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          type="text"
          dir="rtl"
          placeholder={(t("O_TITLE") || "Title") + (t("O_ARABIC") || " (Ar)")}
          value={option?.textAr || ""}
          onChange={(e) =>
            setOptions(
              options.map((o, i) =>
                i === index ? { ...o, textAr: e.target.value } : o,
              ),
            )
          }
        />
      </div>
    </div>
  );
}

QuestionsStep.fields = [];

export default QuestionsStep;
